import { useRef, useState } from 'react';
import { auth, googleProvider } from '../Firebase';
import { signInWithPopup } from 'firebase/auth';
import axios from 'axios';
import config from '../Config';
import { useSetAtom } from 'jotai';
import { setAuthUserAtom, setDataUserAtom } from '../Services/store/atoms';
import { useHistory, useLocation } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { FaSpinner } from 'react-icons/fa';
import { getReferralCode, clearReferralCode } from '../Pages/ReferralCapture';

// role viene de las tabs de Register.jsx cuando el usuario ya eligió cliente/modelo
// ahí mismo. En Login.jsx no se pasa role: el backend crea la cuenta igual (sin
// fricción) pero sin rol, y acá abajo se detecta eso para mandarlo a /choose-role.
function GoogleLoginButton({ darkMode, role, isLoading, setIsLoading }) {
  const history = useHistory();
  const location = useLocation();
  const setAuthUser = useSetAtom(setAuthUserAtom);
  const setDataUser = useSetAtom(setDataUserAtom);
  const [errorMsg, setErrorMsg] = useState(null);

  // window.open "de verdad", capturado una sola vez al montar, antes de que
  // nosotros lo empecemos a envolver para espiar el popup. Es la referencia
  // fija a la que siempre volvemos, sin importar cuántos intentos de login
  // se hayan pisado entre sí.
  const [nativeWindowOpen] = useState(() => window.open);

  // Referencia a la ventana real del popup de Google. Firebase no la expone
  // en su API pública, así que la conseguimos interceptando window.open
  // mientras dura la llamada a signInWithPopup (es el propio SDK el que la
  // invoca internamente para abrir el popup).
  const popupRef = useRef(null);
  // false mientras hay un intento de login en curso. Evita procesar dos
  // veces el mismo resultado/error si el cierre "a mano" y la resolución
  // real de signInWithPopup llegan a pisarse.
  const settledRef = useRef(true);
  const cleanupFocusFallbackRef = useRef(null);
  // El wrapper de window.open que puso el intento actual. Sirve para no
  // pisar el window.open de un intento más nuevo si este todavía anda
  // resolviéndose de fondo cuando ya se lanzó el siguiente.
  const myWrapperRef = useRef(null);

  // Da por cancelado el intento en curso: cierra el popup real de Google (si
  // seguía abierto), devuelve window.open a la normalidad y resetea el
  // spinner. La usan tanto el botón "Cancelar" como la red de seguridad de
  // más abajo, para que ambos caminos limpien exactamente lo mismo — antes
  // la red de seguridad sólo apagaba el spinner y dejaba el popup real
  // colgado de fondo con window.open envuelto para siempre, lo que hacía
  // que el siguiente click no abriera nada hasta recargar la página.
  const cancelInFlightLogin = (message) => {
    if (settledRef.current) return;
    settledRef.current = true;
    if (popupRef.current && !popupRef.current.closed) {
      try { popupRef.current.close(); } catch (e) { /* noop */ }
    }
    popupRef.current = null;
    if (window.open === myWrapperRef.current) window.open = nativeWindowOpen;
    if (cleanupFocusFallbackRef.current) cleanupFocusFallbackRef.current();
    if (setIsLoading) setIsLoading(false);
    setErrorMsg(message);
  };

  // Bug conocido de Firebase Auth: por una restricción de
  // Cross-Origin-Opener-Policy en ciertas versiones de Chrome, signInWithPopup
  // no detecta que el usuario cerró el popup de Google (a veces ni siquiera
  // hace falta cerrarlo del todo, con que pierda el foco alcanza) y la
  // promesa se queda colgada para siempre. Como red de seguridad: si la
  // ventana principal recupera el foco y el login sigue colgado unos
  // segundos después, se lo trata como cancelado.
  const armFocusFallback = () => {
    let fallbackTimeout = null;
    const onWindowFocus = () => {
      fallbackTimeout = setTimeout(() => {
        cancelInFlightLogin('Inicio de sesión con Google cancelado.');
      }, 2000);
    };
    window.addEventListener('focus', onWindowFocus, { once: true });
    cleanupFocusFallbackRef.current = () => {
      window.removeEventListener('focus', onWindowFocus);
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
      cleanupFocusFallbackRef.current = null;
    };
  };

  const handleGoogleLogin = async () => {
    settledRef.current = false;
    popupRef.current = null;
    if (setIsLoading) setIsLoading(true);
    setErrorMsg(null);
    armFocusFallback();

    const wrapper = (...args) => {
      const win = nativeWindowOpen.apply(window, args);
      popupRef.current = win;
      return win;
    };
    myWrapperRef.current = wrapper;
    window.open = wrapper;

    try {
      const result = await signInWithPopup(auth, googleProvider);
      // Ya se canceló desde el botón o por la red de seguridad mientras esto resolvía.
      if (settledRef.current) return;
      settledRef.current = true;
      const idToken = await result.user.getIdToken();

      // Si el usuario llegó por un link de referido (/r/:code), se envía el código
      // para que el backend lo vincule si esta cuenta de Google es nueva.
      const referralCode = getReferralCode();

      const response = await axios.post(
        `${config.REACT_APP_BASE_URL}/api/dasboard/v1/auth/google-login`,
        { id_token: idToken, ...(role ? { role } : {}), ...(referralCode ? { referral_code: referralCode } : {}) },
      );

      // El código sólo se consume cuando se crea una cuenta nueva; en logins de
      // cuentas existentes se conserva por si registran otra cuenta después.
      if (response.data.is_new_user) {
        clearReferralCode();
      }

      axios.defaults.headers.common.Authorization = `Bearer ${response.data.token}`;

      const userData = await axios.get(
        `${config.REACT_APP_BASE_URL}/api/v1/auth/data`
      );
      // setAuthUser recién acá, junto con setDataUser: si isAuth pasara a true antes
      // de tener el perfil fresco, cualquier componente que reaccione a isAuth (como
      // el useEffect de Login.jsx) navegaría con el dataUser viejo todavía en
      // localStorage — por eso aparecía de golpe la pantalla de completar perfil
      // aunque el usuario ya la hubiera llenado antes.
      setDataUser(userData.data);
      setAuthUser(response.data);

      // Si venimos de un AuthRequiredModal con resumeAction (ej. "abrir el
      // chat con esta modelo"), el useEffect de Login.jsx que reacciona a
      // isAuth ya se encarga de resolverlo y pisar esta redirección — no lo
      // duplicamos acá para no disparar el POST a chat/start dos veces en
      // paralelo (ver el mismo comentario en Login.jsx).
      if (location.state?.resumeAction) return;

      if (!userData.data.role) {
        history.push('/choose-role');
      } else if (userData.data.role === 'client') {
        history.push('/client');
      } else if (userData.data.role === 'model') {
        if (!userData.data.name || !userData.data.phone) {
          history.push('/model/complete-registration');
        } else {
          history.push('/model');
        }
      } else if (['admin', 'assistant'].includes(userData.data.role)) {
        history.push('/admin');
      } else {
        history.push('/' + userData.data.role);
      }
    } catch (error) {
      // Ya lo resolvió el botón de cancelar o la red de seguridad — no pisar ese estado.
      if (settledRef.current) return;
      settledRef.current = true;
      console.error('Google login error:', error);
      const msg = error.response?.data?.message || 'No se pudo iniciar sesión con Google.';
      setErrorMsg(msg);
      if (setIsLoading) setIsLoading(false);
    } finally {
      // Sólo restaura si window.open sigue siendo el wrapper de ESTE intento:
      // si ya se lanzó un intento más nuevo, no le pisamos el suyo.
      if (window.open === wrapper) window.open = nativeWindowOpen;
      if (cleanupFocusFallbackRef.current) cleanupFocusFallbackRef.current();
    }
  };

  const handleClick = () => {
    if (isLoading) {
      // El usuario tocó el botón de nuevo mientras el popup de Google seguía
      // abierto (o colgado de fondo): lo cerramos a mano y volvemos el
      // formulario a como estaba antes de tocar "Sign in with Google".
      cancelInFlightLogin(null);
    } else {
      handleGoogleLogin();
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className="ap-btn ap-btn-secondary w-full"
      >
        {isLoading ? (
          <>
            <FaSpinner className="animate-spin text-xl" />
            Cancelar
          </>
        ) : (
          <>
            <FcGoogle className="text-xl" />
            Sign in with Google
          </>
        )}
      </button>
      {errorMsg && (
        <p className="ap-error text-center">{errorMsg}</p>
      )}
    </div>
  );
}

export default GoogleLoginButton;
